import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  alpha,
  useTheme,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Theme,
} from '@mui/material';
import {
  Info,
  Schedule,
  Timer,
  WorkOutline,
  Edit,
  Check,
  Close,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import employeeAttendanceService, { WorkSettings } from '../../services/employeeAttendanceService';
import dateTimeUtils from '../../utils/dateTimeUtils';

// Move SettingRow outside to prevent re-creation on every render
const SettingRow = ({
  icon,
  label,
  value,
  editComponent,
  isEditing,
  theme,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  editComponent: React.ReactNode;
  isEditing: boolean;
  theme: Theme;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      py: 2.5,
      px: 3,
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      },
      transition: 'background-color 0.2s',
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        mr: 2,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 0.5, fontWeight: 500, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
      {isEditing ? (
        editComponent
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

const AttendanceSettings: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const schoolId = user?.schoolId?._id || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState<WorkSettings>({
    workStartTime: '08:00',
    workEndTime: '16:00',
    lateThresholdMinutes: 15,
    halfDayThresholdMinutes: 120,
    minWorkMinutesForFullDay: 360,
    weeklyOffs: [],
  });
  const [editSettings, setEditSettings] = useState<WorkSettings>(settings);

  const fetchSettings = useCallback(async () => {
    if (!schoolId) return;

    try {
      setLoading(true);
      const data = await employeeAttendanceService.getWorkSettings(schoolId);
      if (data) {
        const loadedSettings = {
          workStartTime: data.workStartTime || '08:00',
          workEndTime: data.workEndTime || '16:00',
          lateThresholdMinutes: data.lateThresholdMinutes || 15,
          halfDayThresholdMinutes: data.halfDayThresholdMinutes || 120,
          minWorkMinutesForFullDay: data.minWorkMinutesForFullDay || 360,
          weeklyOffs: data.weeklyOffs || [],
        };
        setSettings(loadedSettings);
        setEditSettings(loadedSettings);
      }
    } catch (error: any) {
      console.error('Error fetching work settings:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleEdit = () => {
    setEditSettings({ ...settings });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditSettings({ ...settings });
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!schoolId) {
      enqueueSnackbar('School ID not found', { variant: 'error' });
      return;
    }

    // Validation
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(editSettings.workStartTime)) {
      enqueueSnackbar('Work start time must be in HH:MM format (24-hour)', { variant: 'error' });
      return;
    }
    if (!timeRegex.test(editSettings.workEndTime)) {
      enqueueSnackbar('Work end time must be in HH:MM format (24-hour)', { variant: 'error' });
      return;
    }
    if (editSettings.lateThresholdMinutes < 0 || editSettings.lateThresholdMinutes > 180) {
      enqueueSnackbar('Late threshold must be between 0 and 180 minutes', { variant: 'error' });
      return;
    }
    if (editSettings.halfDayThresholdMinutes < 0 || editSettings.halfDayThresholdMinutes > 480) {
      enqueueSnackbar('Half-day threshold must be between 0 and 480 minutes', { variant: 'error' });
      return;
    }

    try {
      setSaving(true);
      await employeeAttendanceService.updateWorkSettings(schoolId, editSettings);
      setSettings(editSettings);
      setIsEditing(false);
      enqueueSnackbar('Work settings saved successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error saving work settings:', error);
      enqueueSnackbar(error.message || 'Error saving settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatTime = dateTimeUtils.formatClockTime;

  const formatMinutes = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} minutes`;
    if (m === 0) return `${h} hour${h > 1 ? 's' : ''}`;
    return `${h} hour${h > 1 ? 's' : ''} ${m} minutes`;
  };

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getWeeklyOffsLabel = (weeklyOffs: number[]) => {
    if (!weeklyOffs || weeklyOffs.length === 0) return 'None';
    return weeklyOffs.map(day => DAYS[day]).join(', ');
  };

  if (loading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
            sx={{ borderRadius: 2 }}
          >
            Edit Settings
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Close />}
              onClick={handleCancel}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <SettingRow
          icon={<Schedule fontSize="small" />}
          label="Work Start Time"
          value={formatTime(settings.workStartTime)}
          isEditing={isEditing}
          theme={theme}
          editComponent={
            <TextField
              size="small"
              type="time"
              value={editSettings.workStartTime}
              onChange={(e) => setEditSettings({ ...editSettings, workStartTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="When the work day officially starts"
              sx={{ maxWidth: 400 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkOutline fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<Schedule fontSize="small" />}
          label="Work End Time"
          value={formatTime(settings.workEndTime)}
          isEditing={isEditing}
          theme={theme}
          editComponent={
            <TextField
              size="small"
              type="time"
              value={editSettings.workEndTime}
              onChange={(e) => setEditSettings({ ...editSettings, workEndTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="When the work day officially ends"
              sx={{ maxWidth: 400 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkOutline fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<Timer fontSize="small" />}
          label="Late Threshold"
          value={formatMinutes(settings.lateThresholdMinutes)}
          isEditing={isEditing}
          theme={theme}
          editComponent={
            <TextField
              size="small"
              type="number"
              value={editSettings.lateThresholdMinutes}
              onChange={(e) => setEditSettings({ ...editSettings, lateThresholdMinutes: parseInt(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
              }}
              helperText={`Minutes after work start time to mark as Late (e.g., 15 means late if punch after 08:15)`}
              sx={{ maxWidth: 400 }}
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<Timer fontSize="small" />}
          label="Half-Day Threshold"
          value={formatMinutes(settings.halfDayThresholdMinutes)}
          isEditing={isEditing}
          theme={theme}
          editComponent={
            <TextField
              size="small"
              type="number"
              value={editSettings.halfDayThresholdMinutes}
              onChange={(e) => setEditSettings({ ...editSettings, halfDayThresholdMinutes: parseInt(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
              }}
              helperText="Minutes after work start to mark as Half-Day (e.g., 120 means half-day if 2+ hours late)"
              sx={{ maxWidth: 400 }}
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<WorkOutline fontSize="small" />}
          label="Minimum Work Duration for Full Day"
          value={formatMinutes(settings.minWorkMinutesForFullDay)}
          isEditing={isEditing}
          theme={theme}
          editComponent={
            <TextField
              size="small"
              type="number"
              value={editSettings.minWorkMinutesForFullDay}
              onChange={(e) => setEditSettings({ ...editSettings, minWorkMinutesForFullDay: parseInt(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
              }}
              helperText="Minimum work minutes required for full day credit (e.g., 360 = 6 hours)"
              sx={{ maxWidth: 400 }}
            />
          }
        />

        <Divider />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            py: 2.5,
            px: 3,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            },
            transition: 'background-color 0.2s',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              mr: 2,
              flexShrink: 0,
            }}
          >
            <Schedule fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 500, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
            >
              Weekly Offs
            </Typography>
            {isEditing ? (
              <Box>
                <FormGroup row sx={{ gap: 1 }}>
                  {DAYS.map((day, index) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          size="small"
                          checked={editSettings.weeklyOffs?.includes(index)}
                          onChange={(e) => {
                            const current = editSettings.weeklyOffs || [];
                            const newOffs = e.target.checked
                              ? [...current, index].sort()
                              : current.filter((d) => d !== index);
                            setEditSettings({ ...editSettings, weeklyOffs: newOffs });
                          }}
                        />
                      }
                      label={<Typography variant="body2">{day}</Typography>}
                    />
                  ))}
                </FormGroup>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {getWeeklyOffsLabel(settings.weeklyOffs || [])}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Calculation Preview */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.info.main, 0.05),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Info color="info" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Status Calculation Preview
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Based on your settings, here's how attendance status will be calculated:
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 1 } }}>
          <li>
            <Typography variant="body2">
              <strong>Present:</strong> Punch before {formatTime(settings.workStartTime)} + {settings.lateThresholdMinutes} min ={' '}
              before{' '}
              {(() => {
                const [h, m] = settings.workStartTime.split(':').map(Number);
                const totalMins = h * 60 + m + settings.lateThresholdMinutes;
                const newH = Math.floor(totalMins / 60);
                const newM = totalMins % 60;
                return formatTime(`${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`);
              })()}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Late:</strong> Punch between {settings.lateThresholdMinutes} and {settings.halfDayThresholdMinutes}{' '}
              minutes after {formatTime(settings.workStartTime)}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Half-Day:</strong> Punch {settings.halfDayThresholdMinutes}+ minutes after{' '}
              {formatTime(settings.workStartTime)} (more than {formatMinutes(settings.halfDayThresholdMinutes)} late)
            </Typography>
          </li>
        </Box>
      </Paper>
    </Box>
  );
};

export default AttendanceSettings;
